// Debug Panel Component for Club Penguin Game
// Handles all debug functionality including rectangle drawing tool

interface Rectangle {
  x: number;
  y: number;
  w: number;
  h: number;
  element: HTMLElement;
}

interface DebugPanelOptions {
  stage: HTMLElement;
  onRectanglesChange?: (rectangles: Rectangle[]) => void;
}

export class DebugPanel {
  private stage: HTMLElement;
  private onRectanglesChange?: (rectangles: Rectangle[]) => void;

  // Debug UI elements
  private roomNameEl: HTMLElement | null = null;
  private posDisplay: HTMLElement | null = null;
  private roomDisplay: HTMLElement | null = null;

  // Rectangle drawing tool
  private rectToolActive: boolean = false;
  private rectStartPoint: { x: number; y: number } | null = null;
  private previewRect: HTMLElement | null = null;
  private rectToolBtn: HTMLElement | null = null;
  private rectInstructions: HTMLElement | null = null;
  private saveRectsBtn: HTMLElement | null = null;
  private clearRectsBtn: HTMLElement | null = null;
  private rectItems: HTMLElement | null = null;
  private rectCount: HTMLElement | null = null;
  private rectangles: Rectangle[] = [];

  constructor(options: DebugPanelOptions) {
    this.stage = options.stage;
    this.onRectanglesChange = options.onRectanglesChange;

    this.initializeElements();
    this.setupEventListeners();
  }

  private initializeElements(): void {
    // Get debug UI elements
    this.roomNameEl = document.getElementById('room-name');
    this.posDisplay = document.getElementById('pos-display');
    this.roomDisplay = document.getElementById('room-display');

    // Rectangle tool elements
    this.rectToolBtn = document.getElementById('rect-tool-btn');
    this.rectInstructions = document.getElementById('rect-instructions');
    this.saveRectsBtn = document.getElementById('save-rects-btn');
    this.clearRectsBtn = document.getElementById('clear-rects-btn');
    this.rectItems = document.getElementById('rect-items');
    this.rectCount = document.getElementById('rect-count');
  }

  private setupEventListeners(): void {
    // Rectangle tool buttons
    if (this.rectToolBtn) {
      this.rectToolBtn.addEventListener('click', () => this.toggleRectangleTool());
    }
    if (this.saveRectsBtn) {
      this.saveRectsBtn.addEventListener('click', () => this.saveRectangles());
    }
    if (this.clearRectsBtn) {
      this.clearRectsBtn.addEventListener('click', () => this.clearAllRectangles());
    }

    // Mouse move for rectangle preview
    this.stage.addEventListener('mousemove', (e: MouseEvent) => this.handleMouseMove(e));

    // ESC to cancel rectangle tool
    document.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key === 'Escape' && this.rectToolActive) {
        this.cancelRectangleTool();
      }
    });
  }

  // Public methods for updating debug info
  public updatePosition(x: number, y: number): void {
    if (this.posDisplay) {
      this.posDisplay.textContent = `${Math.round(x)}, ${Math.round(y)}`;
    }
  }

  public updateRoom(roomId: string, roomName: string): void {
    if (this.roomNameEl) {
      this.roomNameEl.textContent = roomName;
    }
    if (this.roomDisplay) {
      this.roomDisplay.textContent = roomId;
    }
  }

  public handleStageClick(x: number, y: number): boolean {
    // Return true if debug panel handled the click, false otherwise
    if (this.rectToolActive) {
      this.handleRectangleToolClick(x, y);
      return true;
    }
    return false;
  }

  // Rectangle drawing tool methods
  private toggleRectangleTool(): void {
    if (this.rectToolActive) {
      this.cancelRectangleTool();
    } else {
      this.activateRectangleTool();
    }
  }

  private activateRectangleTool(): void {
    this.rectToolActive = true;
    this.rectStartPoint = null;

    if (this.rectToolBtn) {
      this.rectToolBtn.textContent = 'Cancel Drawing';
      this.rectToolBtn.classList.add('active');
    }

    if (this.rectInstructions) {
      this.rectInstructions.style.display = 'block';
    }

    console.log('Rectangle tool activated. Click two corners to draw rectangle.');
  }

  private cancelRectangleTool(): void {
    this.rectToolActive = false;
    this.rectStartPoint = null;

    if (this.previewRect) {
      this.previewRect.remove();
      this.previewRect = null;
    }

    if (this.rectToolBtn) {
      this.rectToolBtn.textContent = 'Draw Rectangle';
      this.rectToolBtn.classList.remove('active');
    }

    if (this.rectInstructions) {
      this.rectInstructions.style.display = 'none';
    }

    console.log('Rectangle tool cancelled.');
  }

  private handleMouseMove(e: MouseEvent): void {
    if (!this.rectToolActive || !this.rectStartPoint) return;

    const rect = this.stage.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    this.updatePreviewRect(x, y);
  }

  private updatePreviewRect(endX: number, endY: number): void {
    if (!this.rectStartPoint) return;

    const startX = this.rectStartPoint.x;
    const startY = this.rectStartPoint.y;

    // Calculate rectangle bounds
    const x = Math.min(startX, endX);
    const y = Math.min(startY, endY);
    const w = Math.abs(endX - startX);
    const h = Math.abs(endY - startY);

    if (!this.previewRect) {
      this.previewRect = document.createElement('div');
      this.previewRect.className = 'drawing-rect';
      this.previewRect.style.opacity = '0.7';
      this.stage.appendChild(this.previewRect);
    }

    this.previewRect.style.left = x + 'px';
    this.previewRect.style.top = y + 'px';
    this.previewRect.style.width = w + 'px';
    this.previewRect.style.height = h + 'px';
  }

  private handleRectangleToolClick(x: number, y: number): void {
    if (!this.rectStartPoint) {
      // First click - set start point
      this.rectStartPoint = { x, y };
      console.log(`Rectangle start point: ${x}, ${y}`);
    } else {
      // Second click - complete rectangle
      this.completeRectangle(x, y);
    }
  }

  private completeRectangle(endX: number, endY: number): void {
    if (!this.rectStartPoint) return;

    const startX = this.rectStartPoint.x;
    const startY = this.rectStartPoint.y;

    // Calculate rectangle bounds
    const x = Math.min(startX, endX);
    const y = Math.min(startY, endY);
    const w = Math.abs(endX - startX);
    const h = Math.abs(endY - startY);

    // Don't create tiny rectangles
    if (w < 5 || h < 5) {
      console.log('Rectangle too small, ignoring');
      this.rectStartPoint = null;
      if (this.previewRect) {
        this.previewRect.remove();
        this.previewRect = null;
      }
      return;
    }

    // Create permanent rectangle
    const rectElement = document.createElement('div');
    rectElement.className = 'drawing-rect';
    rectElement.style.left = x + 'px';
    rectElement.style.top = y + 'px';
    rectElement.style.width = w + 'px';
    rectElement.style.height = h + 'px';
    rectElement.style.opacity = '0.5';
    rectElement.title = `${Math.round(x)}, ${Math.round(y)}, ${Math.round(w)}, ${Math.round(h)}`;

    this.stage.appendChild(rectElement);

    // Add to rectangles array
    const rectData: Rectangle = {
      x: Math.round(x),
      y: Math.round(y),
      w: Math.round(w),
      h: Math.round(h),
      element: rectElement,
    };

    this.rectangles.push(rectData);
    this.updateRectangleList();

    // Notify parent component
    if (this.onRectanglesChange) {
      this.onRectanglesChange(this.rectangles);
    }

    // Reset for next rectangle
    this.rectStartPoint = null;
    if (this.previewRect) {
      this.previewRect.remove();
      this.previewRect = null;
    }

    console.log(`Rectangle added: ${rectData.x}, ${rectData.y}, ${rectData.w}, ${rectData.h}`);
  }

  private updateRectangleList(): void {
    if (!this.rectItems || !this.rectCount) return;

    this.rectCount.textContent = this.rectangles.length.toString();

    // Clear existing items
    this.rectItems.innerHTML = '';

    // Add each rectangle to the list
    this.rectangles.forEach((rect, index) => {
      const item = document.createElement('div');
      item.className = 'rect-item';
      item.innerHTML = `
        {x:${rect.x}, y:${rect.y}, w:${rect.w}, h:${rect.h}}
        <button class="delete-btn" data-index="${index}">×</button>
      `;

      // Add delete functionality
      const deleteBtn = item.querySelector('.delete-btn') as HTMLElement;
      deleteBtn.addEventListener('click', () => this.deleteRectangle(index));

      this.rectItems?.appendChild(item);
    });

    // Show/hide save and clear buttons
    const hasRects = this.rectangles.length > 0;
    if (this.saveRectsBtn) {
      this.saveRectsBtn.style.display = hasRects ? 'block' : 'none';
    }
    if (this.clearRectsBtn) {
      this.clearRectsBtn.style.display = hasRects ? 'block' : 'none';
    }
  }

  private deleteRectangle(index: number): void {
    if (index >= 0 && index < this.rectangles.length) {
      // Remove element from stage
      this.rectangles[index].element.remove();

      // Remove from array
      this.rectangles.splice(index, 1);

      // Update list
      this.updateRectangleList();

      // Notify parent component
      if (this.onRectanglesChange) {
        this.onRectanglesChange(this.rectangles);
      }
    }
  }

  private saveRectangles(): void {
    if (this.rectangles.length === 0) {
      alert('No rectangles to save!');
      return;
    }

    // Create array string
    const rectArray = this.rectangles
      .map(rect => `{ x: ${rect.x}, y: ${rect.y}, w: ${rect.w}, h: ${rect.h} }`)
      .join(',\n  ');

    const fullArray = `[\n  ${rectArray}\n]`;

    // Copy to clipboard
    this.copyToClipboard(fullArray);

    alert(
      `${this.rectangles.length} rectangles copied to clipboard!\n\nReady to paste into your room config.`,
    );
  }

  private clearAllRectangles(): void {
    if (this.rectangles.length === 0) return;

    if (confirm(`Delete all ${this.rectangles.length} rectangles?`)) {
      // Remove all elements
      this.rectangles.forEach(rect => rect.element.remove());

      // Clear array
      this.rectangles = [];

      // Update list
      this.updateRectangleList();

      // Notify parent component
      if (this.onRectanglesChange) {
        this.onRectanglesChange(this.rectangles);
      }

      console.log('All rectangles cleared');
    }
  }

  private async copyToClipboard(text: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(text);
      console.log('Copied to clipboard:', text);
    } catch (error) {
      console.warn('Failed to copy to clipboard:', error);
      // Fallback: show in console for manual copy
      console.log('Copy manually:', text);
    }
  }

  // Public method to get current rectangles
  public getRectangles(): Rectangle[] {
    return [...this.rectangles];
  }

  // Public method to clear rectangles (for room changes)
  public clearRectangles(): void {
    this.rectangles.forEach(rect => rect.element.remove());
    this.rectangles = [];
    this.updateRectangleList();
  }

  // Clean up method
  public destroy(): void {
    this.clearAllRectangles();
    this.cancelRectangleTool();
  }
}
